import DOMPurify from 'dompurify';
import logger from '../core/logger';
import { isNative } from '../core/platform';

// Mobile device detection and optimization utilities

export const detectMobileDevice = () => {
  const userAgent = navigator.userAgent;
  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
  const isAndroid = /Android/i.test(userAgent);
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  
  return {
    isMobile,
    isIOS,
    isAndroid,
    hasTouch,
    isStandalone,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    }
  };
};

export const getMobileCapabilities = async () => {
  const device = detectMobileDevice();
  
  // Check for camera support
  let hasCamera = false;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    hasCamera = devices.some(device => device.kind === 'videoinput');
  } catch (error) {
    logger.warn('Could not check camera availability:', error);
  }
  
  // Check for other capabilities
  const hasVibration = 'vibrate' in navigator;
  const hasGeolocation = 'geolocation' in navigator;
  const hasFileAPI = 'FileReader' in window;
  const hasCanvas = !!document.createElement('canvas').getContext;
  
  return {
    ...device,
    hasCamera,
    hasVibration,
    hasGeolocation,
    hasFileAPI,
    hasCanvas
  };
};

export const optimizeImageForMobile = (file, options = {}) => {
  const {
    maxWidth = 1200,
    maxHeight = 1200,
    quality = 0.8,
    format = 'image/jpeg'
  } = options;
  
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate optimal dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Apply mobile-optimized settings
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(resolve, format, quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

export async function triggerHapticFeedback(type = 'light') {
  if (isNative()) {
    try {
      const { Haptics, ImpactStyle, NotificationType } = await import('@capacitor/haptics');
      const styleMap = {
        light: () => Haptics.impact({ style: ImpactStyle.Light }),
        medium: () => Haptics.impact({ style: ImpactStyle.Medium }),
        heavy: () => Haptics.impact({ style: ImpactStyle.Heavy }),
        success: () => Haptics.notification({ type: NotificationType.Success }),
        error: () => Haptics.notification({ type: NotificationType.Error }),
        double: () => Haptics.impact({ style: ImpactStyle.Light }),
      };
      await (styleMap[type] || styleMap.light)();
    } catch (e) {
      // Haptics not available — silent fail
    }
    return;
  }

  // Web-only: vibrate API fallback (navigator.vibrate not supported on iOS)
  if (!isNative() && 'vibrate' in navigator) {
    const patterns = {
      light: [10],
      medium: [30],
      heavy: [50],
      double: [10, 50, 10],
      success: [10, 30, 10],
      error: [100, 50, 100, 50, 100]
    };

    const pattern = patterns[type] || patterns.light;
    navigator.vibrate(pattern);
  }
}

export const preventMobileZoom = () => {
  // Prevent double-tap zoom
  let lastTouchEnd = 0;
  document.addEventListener('touchend', (event) => {
    const now = (new Date()).getTime();
    if (now - lastTouchEnd <= 300) {
      event.preventDefault();
    }
    lastTouchEnd = now;
  }, { passive: false });
  
  // Prevent pinch zoom
  document.addEventListener('touchmove', (event) => {
    if (event.scale !== 1) {
      event.preventDefault();
    }
  }, { passive: false });
};

export const optimizeMobileKeyboard = (element) => {
  if (!element) return;
  
  const device = detectMobileDevice();
  
  // iOS specific optimizations
  if (device.isIOS) {
    element.style.fontSize = '16px'; // Prevent zoom on focus
    element.setAttribute('autocomplete', 'off');
    element.setAttribute('autocorrect', 'on');
    element.setAttribute('autocapitalize', 'sentences');
    element.setAttribute('spellcheck', 'true');
  }
  
  // Android specific optimizations
  if (device.isAndroid) {
    element.style.fontSize = '16px';
    element.setAttribute('inputmode', 'text');
  }
  
  // Handle viewport changes when keyboard appears
  const handleResize = () => {
    if (device.isMobile && document.activeElement === element) {
      // Scroll element into view when keyboard appears
      setTimeout(() => {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }, 300);
    }
  };
  
  window.addEventListener('resize', handleResize);
  
  return () => {
    window.removeEventListener('resize', handleResize);
  };
};

export const getMobileImageConstraints = (facingMode = 'environment') => {
  const device = detectMobileDevice();

  const baseConstraints = {
    video: {
      facingMode: { ideal: facingMode },
      aspectRatio: { ideal: 16/9 }
    }
  };

  if (device.isMobile) {
    // Mobile-optimized constraints
    baseConstraints.video.width = { ideal: 1280, max: 1920 };
    baseConstraints.video.height = { ideal: 720, max: 1080 };
    baseConstraints.video.frameRate = { ideal: 30, max: 30 };
  } else {
    // Desktop constraints
    baseConstraints.video.width = { ideal: 1920 };
    baseConstraints.video.height = { ideal: 1080 };
  }

  return baseConstraints;
};

export const handleMobileOrientation = () => {
  const updateOrientation = () => {
    const orientation = screen.orientation?.angle || 0;
    document.documentElement.style.setProperty('--orientation-angle', `${orientation}deg`);
    
    // Trigger haptic feedback on orientation change
    triggerHapticFeedback('light');
  };
  
  if (screen.orientation) {
    screen.orientation.addEventListener('change', updateOrientation);
    updateOrientation();
  }
  
  return () => {
    if (screen.orientation) {
      screen.orientation.removeEventListener('change', updateOrientation);
    }
  };
};

export const createMobileToast = (message, type = 'info', duration = 3000) => {
  const toast = document.createElement('div');
  toast.className = `fixed top-4 left-4 right-4 z-50 p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
    type === 'success' ? 'bg-green-600 text-white' :
    type === 'error' ? 'bg-red-600 text-white' :
    type === 'warning' ? 'bg-yellow-600 text-white' :
    'bg-blue-600 text-white'
  }`;
  
  const safeMessage = DOMPurify.sanitize(message);
  toast.innerHTML = `
    <div class="flex items-center gap-3">
      <span class="text-xl">
        ${type === 'success' ? '✅' :
          type === 'error' ? '❌' :
          type === 'warning' ? '⚠️' : 'ℹ️'}
      </span>
      <span class="flex-1">${safeMessage}</span>
    </div>
  `;
  
  document.body.appendChild(toast);
  
  // Trigger haptic feedback
  triggerHapticFeedback(type === 'error' ? 'error' : 'light');
  
  // Animate in
  requestAnimationFrame(() => {
    toast.style.transform = 'translateY(0)';
  });
  
  // Auto remove
  setTimeout(() => {
    toast.style.transform = 'translateY(-100%)';
    toast.style.opacity = '0';
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  }, duration);
  
  return toast;
};

export const addMobileSwipeGestures = (element, callbacks = {}) => {
  if (!element) return;
  
  let startX = 0;
  let startY = 0;
  let startTime = 0;
  
  const handleTouchStart = (e) => {
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    startTime = Date.now();
  };
  
  const handleTouchEnd = (e) => {
    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;
    const endTime = Date.now();
    
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const deltaTime = endTime - startTime;
    
    // Only register as swipe if it's fast enough and far enough
    if (deltaTime < 300 && Math.abs(deltaX) > 50) {
      if (deltaX > 0 && callbacks.onSwipeRight) {
        callbacks.onSwipeRight();
        triggerHapticFeedback('light');
      } else if (deltaX < 0 && callbacks.onSwipeLeft) {
        callbacks.onSwipeLeft();
        triggerHapticFeedback('light');
      }
    }
    
    if (deltaTime < 300 && Math.abs(deltaY) > 50) {
      if (deltaY > 0 && callbacks.onSwipeDown) {
        callbacks.onSwipeDown();
      } else if (deltaY < 0 && callbacks.onSwipeUp) {
        callbacks.onSwipeUp();
      }
    }
  };
  
  element.addEventListener('touchstart', handleTouchStart, { passive: true });
  element.addEventListener('touchend', handleTouchEnd, { passive: true });
  
  return () => {
    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchend', handleTouchEnd);
  };
};